//////////////////
// IMPORTS
//////////////////
// libraries
// mutation
// util
import { setPricingDialogOpen } from "#webapp/src/state/actions/payments.actions";
// material-ui
// import { Typography } from "@material-ui/core";
import {
  PlanStatusCallout,
  PlanStatusCalloutType,
} from "#webapp/src/components/plan-status-callout";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { TrackingEvents, track } from "#webapp/src/util/analytics.util";
import { getValue } from "#webapp/src/util/errorable.util";
import { EFlex, isAutopilotExplicitlyEnabled } from "#webapp/src/util/event-tag.util";
import {
  isApPausedFromFeatureGrid,
  isMoveRangeEnabledFromFeatureGrid,
} from "#webapp/src/util/event.util";
import { EAutopilotWarning } from "@clockwise/client-commons/src/util/event-tag";
import { DayOffsetTimeSlot } from "@clockwise/client-commons/src/util/time-slot";
import { AutopilotEventStatusEnum, Day } from "@clockwise/schema";
import { AutopilotFlexibility } from "@clockwise/web-commons/src/ui/autopilot-flexibility";
import { shouldShowJoinExistingPlanWithRedux } from "@clockwise/web-commons/src/util/feature-grid.util";
import { OVER_EXPIRED_DAYS } from "@clockwise/web-commons/src/util/trial-constants";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMonetization } from "../../hooks/useMonetization";
import { IProps } from "./AutopilotFormTypes";
//////////////////
// COMPONENT
//////////////////
export const AutopilotForm = (props: IProps) => {
  const { canUserAccessFlexMeetings } = useMonetization();

  const featureGrid = useSelector((state: IReduxState) => state.featureGrid);
  const orgBillingGroupIds = useSelector((state: IReduxState) => state.payments.orgBillingGroupIds);

  const dispatch = useDispatch();

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const getDefaultFlex = () => {
    const { event } = props;

    return (
      props.defaultFlex ??
      ((event &&
        event.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        ((event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings
          .movementType as unknown) as EFlex)) ||
        EFlex.Day)
    );
  };

  const isToggledOn = () => {
    const { flex } = props.formState;
    return flex !== EFlex.Pin;
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  const onToggle = () => {
    // important!!
    // if currently on, toggling turns autopilot off, which pins it
    const flex = isToggledOn() ? EFlex.Pin : getDefaultFlex();
    props.onChange({
      ...props.formState,
      flex,
    });
  };

  const onChangeFlex = (flex: EFlex, daysOfWeek: Day[] = []) => {
    props.onChange({
      ...props.formState,
      flex,
      daysOfWeek,
    });
  };

  const onChangeRoom = () => {
    props.onChange({
      ...props.formState,
      allowSimilarRooms: !props.formState.allowSimilarRooms,
    });
  };

  const onChangeTime = (
    allowedStart: DayOffsetTimeSlot | null,
    allowedEnd: DayOffsetTimeSlot | null,
  ) => {
    props.onChange({
      ...props.formState,
      timeRange: { allowedStart, allowedEnd },
    });
  };

  const onClickViewPlans = () => {
    dispatch(setPricingDialogOpen(true));
    track(TrackingEvents.PAYWALLS.MOVE_RANGES_CLICK_VIEW_PLANS);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  const shouldShowJoinExistingPlanToUser = () => {
    return shouldShowJoinExistingPlanWithRedux(orgBillingGroupIds, featureGrid.hasPaidPlan);
  };

  const {
    showRoom,
    formState,
    isDisabled,
    event,
    dayOnOffMap,
    showDayOfWeekToggle,
    surface,
  } = props;

  const getWarning = () => {
    const { warning } = props;
    if (!!warning) {
      // if warning is OrganizerApPaused then show even if event is not toggled on
      if (warning === EAutopilotWarning.OrganizerApPaused) {
        return warning;
      } else if (!isToggledOn()) {
        return warning;
      }
    }
    return undefined;
  };

  const intersectionOfAttendeesMeetingHours =
    getValue(event?.suggestedAutopilotSettingsResponseV3)?.intersectionOfAttendeesMeetingHours ||
    null;

  // we render a fixed autopilotFlexibility for events
  // that have an AP status of Paused or have AP paused from the feature grid
  // or that cannot move because attendee meeting hours don't intersect
  const apStatus = getValue(event?.autopilotEventStatus)?.status;

  const eventFixed = event
    ? (isApPausedFromFeatureGrid(event) ||
        apStatus === AutopilotEventStatusEnum.Paused ||
        !intersectionOfAttendeesMeetingHours) &&
      isToggledOn()
    : false;

  const isPlanRestricted = () => {
    const { canUserAccessFlexMeetings } = props;

    if (!canUserAccessFlexMeetings) {
      return false;
    } else {
      return event ? !isMoveRangeEnabledFromFeatureGrid(event) : false;
    }
  };

  const getPlanStatusTrackingEvent = () => {
    const { trialExpirationStatus } = props;
    const showJoinExistingPlan = shouldShowJoinExistingPlanToUser();
    if (showJoinExistingPlan) {
      return trialExpirationStatus?.isTrialExpired
        ? TrackingEvents.TRIAL.FLEX_MEETING_SIDEBAR_VIEW_TRIAL_EXPIRED_JOIN_PLAN_CLICKED
        : TrackingEvents.TRIAL.FLEX_MEETING_SIDEBAR_VIEW_ONE_DAY_LEFT_JOIN_PLAN_CLICKED;
    } else {
      return trialExpirationStatus?.isTrialExpired
        ? TrackingEvents.TRIAL.FLEX_MEETING_SIDEBAR_VIEW_TRIAL_EXPIRED_UPGRADE_CLICKED
        : TrackingEvents.TRIAL.FLEX_MEETING_SIDEBAR_VIEW_ONE_DAY_LEFT_UPGRADE_CLICKED;
    }
  };

  const shouldShowPlanStatusCallout = () => {
    const { event, trialExpirationStatus } = props;
    const isTrialOneDayLeftOrExpired =
      trialExpirationStatus?.timeLeftInTrial === 1 || trialExpirationStatus?.isTrialExpired;
    const isHold = !!event ? event.isHold : false;
    const flexEnabled = !!event && isAutopilotExplicitlyEnabled(event);
    return isTrialOneDayLeftOrExpired && !isHold && flexEnabled;
  };

  const getPlanCalloutText = () => {
    const trialStatus = props.trialExpirationStatus;
    if (trialStatus?.isTrialExpired) {
      if (trialStatus?.timeLeftInTrial && trialStatus?.timeLeftInTrial < OVER_EXPIRED_DAYS) {
        return PlanStatusCalloutType.TrialOverExpired_FlexMeeting;
      } else {
        return PlanStatusCalloutType.TrialExpired_FlexMeeting;
      }
    } else {
      return PlanStatusCalloutType.TrialOneDayLeft_FlexMeeting;
    }
  };

  return (
    <>
      {shouldShowPlanStatusCallout() && (
        <div className="cw-mb-4">
          <PlanStatusCallout
            calloutType={getPlanCalloutText()}
            showJoinExistingPlan={shouldShowJoinExistingPlanToUser()}
            trackingEvent={getPlanStatusTrackingEvent()}
          />
        </div>
      )}
      <AutopilotFlexibility
        event={props.event ?? null}
        org={props.org ?? null}
        selectedDate={props.selectedDate}
        onChangeFlex={onChangeFlex}
        onChangeRoom={onChangeRoom}
        onChangeTime={onChangeTime}
        selectedFlex={formState.flex}
        trialExpirationStatus={props.trialExpirationStatus}
        allowSimilarRooms={!!formState.allowSimilarRooms}
        disabled={isDisabled}
        showRoom={showRoom}
        onClickViewPlans={onClickViewPlans}
        allowedStart={formState.timeRange?.allowedStart}
        allowedEnd={formState.timeRange?.allowedEnd}
        planRestricted={isPlanRestricted()}
        isExtension={false}
        onToggle={onToggle}
        warning={getWarning()}
        fixed={eventFixed}
        attendeeMeetingHoursIntersect={!!intersectionOfAttendeesMeetingHours}
        showDayOfWeekToggle={showDayOfWeekToggle}
        dayOnOffMap={dayOnOffMap}
        isAutopilotEnabled={isToggledOn()}
        surface={surface}
        showJoinExistingPlan={shouldShowJoinExistingPlanToUser()}
        canUserAccessFlexMeetings={canUserAccessFlexMeetings}
      />
    </>
  );
};
